/* eslint-disable import/prefer-default-export */
import { logDevMessage, validateLatLong, parseJSON } from 'utils/utils';

/**
 * @param state location state as defined in locationSlice
 * @param action.payload.defaultLocation location from client or network config. constructed in 'configUtils > getNetworkDefaultLocation'
 * @param {string} action.payload.defaultLocation.city
 * @param {string} action.payload.defaultLocation.state
 * @param {string} action.payload.defaultLocation.lat
 * @param {string} action.payload.defaultLocation.long
 */
export function handleSetDefaultLocation(state, action) {
  const { defaultLocation } = action?.payload || {};
  if (!defaultLocation) return;

  const { city, state: usState, lat, long } = defaultLocation;
  if (!city || !usState || !lat || !long) {
    logDevMessage("Malformed location passed to 'handleSetDefaultLocation'");
    return;
  }

  state.locationInput = `${city}, ${usState}`;
  state.latLong = { latitude: lat, longitude: long };
  state.locationComponents = { city, state: usState };
  state.locationResolved = true;
}

export function isValidLocation(location) {
  if (!location) return false;
  const { locationComponents, latLong, locationInput } = location;

  const validLocationComponents = [
    locationInput && true,
    locationComponents?.city && true,
    locationComponents?.state && true,
    validateLatLong(latLong.latitude, latLong.longitude),
  ];

  const filteredLocationComponents = validLocationComponents.filter(Boolean);

  return validLocationComponents.length === filteredLocationComponents.length;
}

export function getStoredLocationHistory() {
  const locationHistory = parseJSON(localStorage.getItem('locationHistory')) || [];
  return locationHistory.filter(isValidLocation);
}

/**
 * This response will include additional fields that we do not use.
 * @typedef {{addresses:{address: GeocodedAddress}[]}} GeocodeResult - JSON converted atlas API response.
 * @typedef {{
 *   countrySubdivision: string,
 *   countrySecondarySubdivision: string,
 *   countryCodeISO3: string,
 *   municipality: string,
 *   neighborhood: string
 *   postalCode: string,
 *   streetName: string,
 *   streetNameAndNumber: string,
 * }} GeocodedAddress
 * {
 *   countrySubdivision - State or Province (abbreviated),
 *   countrySecondarySubdivision - County,
 *   countryCodeISO3 - country/region name (abbreviated),
 *   municipality - City / Town
 *   neighbourhood - Neighbourhood (british spelling)
 *   postalCode - ZIP code (5 digit)
 *   streetName - ex: "Arapahoe Street"
 *   streetNameAndNumber - ex: "940 Arapahoe Street"
 * }
 */

/**
 * @param {{results:GeocodeResult[]}} response
 * @returns {{address: string, neighbourhood: string, city: string, state: string, fullAddress: string}}
 */
export function formatGeolocate(response) {
  const { addresses } = response;
  if (!addresses?.length) return null;
  if (!addresses[0]?.address) return null;

  const {
    countrySubdivision,
    countrySecondarySubdivision,
    countryCodeISO3,
    municipality,
    neighbourhood,
    postalCode,
    streetName,
    streetNameAndNumber,
    freeformAddress,
  } = addresses[0].address;

  const isOutsideOfUSA = countryCodeISO3 !== 'USA';

  const fullAddress =
    streetNameAndNumber && municipality
      ? `${streetNameAndNumber}, ${municipality}${isOutsideOfUSA ? ` ${countryCodeISO3}` : ''}`
      : null;

  return {
    state: countrySubdivision || null,
    city: municipality || null,
    zip: postalCode || null,
    county: countrySecondarySubdivision || null,
    country: countryCodeISO3 || null,
    neighbourhood: neighbourhood || null,
    street: streetName || null,
    fullAddress: fullAddress || freeformAddress,
    isOutsideOfUSA,
  };
}
